import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Button } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Modal } from "ui/Modal"
import { getCategories } from "store/trips"
import styles from "./index.module.css"

const DURATION_TYPES = [
  { value: "single_day", labelKey: "experiences.filters.single_day", fallback: "Single Day" },
  { value: "multi_day", labelKey: "experiences.filters.multi_day", fallback: "Multi Day" }
]

const FilterDropdown = ({ open, onClose, onApply, onClearAll, selectedCategoryIds, selectedDurationTypes }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const categories = useSelector((state) => state.trips.categories.data?.categories || [])

  const [draftCategories, setDraftCategories] = useState(selectedCategoryIds)
  const [draftDurationTypes, setDraftDurationTypes] = useState(selectedDurationTypes)

  useEffect(() => {
    if (open && categories.length === 0) dispatch(getCategories())
  }, [open, categories.length, dispatch])

  useEffect(() => {
    if (open) {
      setDraftCategories(selectedCategoryIds)
      setDraftDurationTypes(selectedDurationTypes)
    }
  }, [open, selectedCategoryIds, selectedDurationTypes])

  const toggleCategory = (id) => {
    setDraftCategories((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
  }

  const toggleDurationType = (value) => {
    setDraftDurationTypes((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]))
  }

  const handleClearAll = () => {
    setDraftCategories([])
    setDraftDurationTypes([])
    onClearAll?.()
    onClose?.()
  }

  const handleApply = () => {
    onApply?.({ categoryIds: draftCategories, durationTypes: draftDurationTypes })
    onClose?.()
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      title={t("experiences.filters.title", { defaultValue: "Filters" })}
      width={520}
      footer={
        <div className={styles.footer}>
          <Button color="primary" variant="outlined" size="large" shape="round" className={styles.footerButton} onClick={handleClearAll}>
            {t("experiences.clear_all")}
          </Button>
          <Button type="primary" size="large" shape="round" className={styles.footerButton} onClick={handleApply}>
            {t("experiences.filters.show_results", { defaultValue: "Show results" })}
          </Button>
        </div>
      }
    >
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("experiences.filters.categories", { defaultValue: "Categories" })}</div>
        <div className={styles.chipGroup}>
          {categories.map((cat) => {
            const active = draftCategories.includes(cat.id)
            return (
              <button
                key={cat.id}
                type="button"
                className={`${styles.chip} ${active ? styles.chipActive : ""}`}
                onClick={() => toggleCategory(cat.id)}
                aria-pressed={active}
              >
                {cat.name}
              </button>
            )
          })}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("experiences.filters.duration_type", { defaultValue: "Duration Type" })}</div>
        <div className={styles.chipGroup}>
          {DURATION_TYPES.map((dt) => {
            const active = draftDurationTypes.includes(dt.value)
            return (
              <button
                key={dt.value}
                type="button"
                className={`${styles.chip} ${active ? styles.chipActive : ""}`}
                onClick={() => toggleDurationType(dt.value)}
                aria-pressed={active}
              >
                {t(dt.labelKey, { defaultValue: dt.fallback })}
              </button>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

FilterDropdown.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClearAll: PropTypes.func,
  selectedCategoryIds: PropTypes.arrayOf(PropTypes.number),
  selectedDurationTypes: PropTypes.arrayOf(PropTypes.string)
}

FilterDropdown.defaultProps = {
  onClearAll: () => {},
  selectedCategoryIds: [],
  selectedDurationTypes: []
}

export default FilterDropdown
